import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { LifecycleFocus, splitRegisteredName } from '../lifecycle';
import { AutolabelJobForm } from './AutolabelJobForm';
import { InferenceComparisonJobForm } from './InferenceComparisonJobForm';
import { McapJobForm } from './McapJobForm';
import { FormPicker } from './FormPicker';
import { JobDatasets } from './JobDatasets';
import { SpecForm } from './SpecForm';
import { Badge, Button, Card, EmptyState, SegmentedControl, tokens } from './ui/controls';

type KindId = 'train' | 'test' | 'autolabel' | 'inference-comparison' | 'mcap-evaluation';

const KINDS: Array<{ id: KindId; path: string; label: string; templateKind: string }> = [
  { id: 'train', path: 'train', label: 'Train', templateKind: 'train' },
  { id: 'test', path: 'test', label: 'Test', templateKind: 'test' },
  { id: 'autolabel', path: 'autolabel', label: 'Autolabel', templateKind: 'autolabel' },
  {
    id: 'inference-comparison',
    path: 'inference-comparison',
    label: 'Inference comparison',
    templateKind: 'inference_comparison',
  },
  { id: 'mcap-evaluation', path: 'mcap-evaluation', label: 'MCAP', templateKind: 'mcap' },
];

function statusTone(status: string) {
  const value = status.toLowerCase();
  if (value.includes('fail') || value.includes('error') || value.includes('stopped')) return 'danger';
  if (value.includes('complete') || value.includes('success')) return 'success';
  if (value.includes('progress') || value.includes('running')) return 'info';
  if (value.includes('submit') || value.includes('pending') || value.includes('queue')) return 'warning';
  return 'neutral';
}

function formatTimestamp(value: unknown) {
  if (!value) return null;
  return String(value).replace('T', ' ').replace(/\.\d+$/, '');
}

const linkStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.3rem 0.65rem',
  borderRadius: tokens.radius,
  border: `1px solid ${tokens.border}`,
  background: tokens.surface,
  color: tokens.primary,
  fontSize: '0.8rem',
  textDecoration: 'none',
};

type LinkedModel = {
  name: string;
  version: string;
  lifecycle_stage: string;
  run_id: string;
};

function datasetKey(name: string) {
  return name.replace(/[^A-Za-z0-9_]/g, '_') || 'dataset';
}

export default function JobsTab({
  focus,
  onFocusConsumed,
  onInspect,
}: {
  focus: LifecycleFocus | null;
  onFocusConsumed: () => void;
  onInspect: (modelName: string, version: string) => void;
}) {
  const [kindId, setKindId] = useState<KindId>('train');
  const [spec, setSpec] = useState<Record<string, unknown>>({});
  const [jobs, setJobs] = useState<Array<Record<string, unknown>>>([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [openJobs, setOpenJobs] = useState<Record<string, boolean>>({});

  const kind = useMemo(() => KINDS.find((k) => k.id === kindId) ?? KINDS[0], [kindId]);

  const refresh = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const res = await api.dataviewer.listJobs();
      setJobs((res.data as { jobs: Array<Record<string, unknown>> }).jobs || []);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const inflight = jobs.some((job) => {
    const kind = String(job.kind ?? '');
    const status = String(job.status ?? '').toLowerCase();
    return (kind === 'train' || kind === 'test') && !['completed', 'failed', 'stopped'].includes(status);
  });

  useEffect(() => { refresh().catch((e) => setMessage(String(e))); }, [refresh]);
  useEffect(() => {
    if (!inflight) return undefined;
    const timer = window.setInterval(() => {
      refresh().catch((e) => setMessage(String(e)));
    }, 8000);
    return () => window.clearInterval(timer);
  }, [inflight, refresh]);
  useEffect(() => {
    if (!focus || focus.tab !== 'jobs') return;
    if (focus.mode === 'train') {
      const key = datasetKey(focus.datasetName);
      setKindId('train');
      setSpec({
        datasets: {
          [key]: { mode: 'sql', name: focus.datasetName, version: focus.datasetVersion },
        },
      });
    } else {
      setKindId('test');
      setSpec({
        model: {
          selection_method: 'mlflow_version',
          mlflow: {
            species: focus.species,
            model_type: focus.modelType,
            version: focus.version,
          },
        },
      });
    }
    onFocusConsumed();
  }, [focus, onFocusConsumed]);

  const applySpec = (next: Record<string, unknown>) => {
    setSpec(next);
  };

  const submit = async () => {
    setSubmitting(true);
    setMessage('');
    try {
      const res = await api.dataviewer.submitJob(kind.path, spec);
      const data = res.data as { status?: string; detail?: string };
      setMessage(
        data.status === 'queued'
          ? data.detail || 'Queued until the current SageMaker job finishes.'
          : `Submitted: ${JSON.stringify(res.data)}`,
      );
      await refresh();
    } catch (e: any) {
      setMessage(e.response?.data?.detail || e.message || 'Submission failed');
      await refresh().catch(() => undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Jobs</h2>

      <Card title="Submit a job">
        <div style={{ display: 'grid', gap: '1rem' }}>
          <SegmentedControl
            options={KINDS.map((k) => ({ id: k.id, label: k.label }))}
            value={kindId}
            onChange={(id) => {
              setKindId(id);
              setSpec({});
            }}
          />

          <FormPicker kind={kind.templateKind} spec={spec} onSpec={applySpec} />

          {kindId === 'autolabel' ? (
            <AutolabelJobForm spec={spec} onChange={setSpec} />
          ) : kindId === 'inference-comparison' ? (
            <InferenceComparisonJobForm spec={spec} onChange={setSpec} />
          ) : kindId === 'mcap-evaluation' ? (
            <McapJobForm spec={spec} onChange={setSpec} />
          ) : (
            <SpecForm kind={kind.templateKind} spec={spec} onChange={setSpec} />
          )}

          {(kindId === 'train' || kindId === 'test') && (
            <JobDatasets spec={spec} onChange={setSpec} />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting…' : `Submit ${kind.label} to SageMaker`}
            </Button>
            {message && <span style={{ fontSize: '0.85rem', color: tokens.textMuted }}>{message}</span>}
          </div>
        </div>
      </Card>

      <Card
        title="Recent jobs"
        actions={
          <Button onClick={() => refresh()} disabled={loadingJobs}>
            {inflight ? 'Updating SageMaker status…' : loadingJobs ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      >
        {jobs.length === 0 ? (
          <EmptyState>No jobs submitted yet.</EmptyState>
        ) : (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {jobs.map((job) => {
              const jobId = String(job.job_id);
              const open = Boolean(openJobs[jobId]);
              const artifacts = Array.isArray(job.artifacts) ? (job.artifacts as Array<{ id: string }>) : [];
              const created = formatTimestamp(job.created_at);
              const started = formatTimestamp(job.started_at);
              const finished = formatTimestamp(job.finished_at);
              const links = (job.links || {}) as { sagemaker?: string; cloudwatch?: string };
              const linked = job.model as LinkedModel | undefined;
              const parsedModel = linked ? splitRegisteredName(linked.name) : null;
              const details = [
                ['SageMaker', String(job.sagemaker_job_name || '')],
                ['MLflow', String(job.mlflow_run_id || '')],
                ['Submitted by', String(job.submitted_by || '')],
                ['Started', started || ''],
                ['Finished', finished || ''],
                ['Output', String(job.output_s3_prefix || '')],
              ].filter((pair): pair is [string, string] => Boolean(pair[1]));
              return (
                <div
                  key={jobId}
                  style={{
                    border: `1px solid ${tokens.border}`,
                    borderRadius: tokens.radius,
                    padding: '0.75rem 0.9rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <Button
                      variant="subtle"
                      aria-expanded={open}
                      aria-label={open ? 'Hide job details' : 'Show job details'}
                      onClick={() => setOpenJobs((prev) => ({ ...prev, [jobId]: !prev[jobId] }))}
                      style={{ padding: '0.1rem 0.35rem', fontSize: '0.85rem', lineHeight: 1 }}
                    >
                      {open ? '▾' : '▸'}
                    </Button>
                    <Badge tone={statusTone(String(job.status ?? ''))}>{String(job.status ?? 'unknown')}</Badge>
                    <strong style={{ fontSize: '0.95rem' }}>{String(job.name || job.job_id)}</strong>
                    <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>{String(job.kind ?? '')}</span>
                    {created && (
                      <span style={{ fontSize: '0.8rem', color: tokens.textFaint, marginLeft: 'auto' }}>{created}</span>
                    )}
                  </div>

                  {String(job.status ?? '').toLowerCase() === 'queued' ? (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: tokens.textMuted }}>
                      Waiting for a free SageMaker instance.
                    </p>
                  ) : null}

                  {job.error_message ? (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: tokens.danger }}>
                      {String(job.error_message)}
                    </p>
                  ) : null}

                  {open && (
                    <div style={{ marginTop: '0.7rem', display: 'grid', gap: '0.6rem' }}>
                      {details.length > 0 && (
                        <dl
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr',
                            gap: '0.15rem 0.75rem',
                            margin: 0,
                            fontSize: '0.82rem',
                            color: tokens.textMuted,
                          }}
                        >
                          {details.map(([label, value]) => (
                            <React.Fragment key={label}>
                              <dt style={{ fontWeight: 600 }}>{label}</dt>
                              <dd style={{ margin: 0, wordBreak: 'break-all' }}>{value}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      )}
                      {links.sagemaker || links.cloudwatch ? (
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {links.sagemaker && (
                            <a href={links.sagemaker} target="_blank" rel="noreferrer" style={linkStyle}>
                              SageMaker console
                            </a>
                          )}
                          {links.cloudwatch && (
                            <a href={links.cloudwatch} target="_blank" rel="noreferrer" style={linkStyle}>
                              CloudWatch logs
                            </a>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
                          SageMaker and CloudWatch links appear once this job starts.
                        </span>
                      )}
                      {linked ? (
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.82rem' }}>
                            {linked.name} v{linked.version}
                          </span>
                          {linked.lifecycle_stage ? <Badge tone="info">{linked.lifecycle_stage}</Badge> : null}
                          {parsedModel ? (
                            <Button
                              style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                              onClick={() => {
                                setKindId('test');
                                setSpec({
                                  model: {
                                    selection_method: 'mlflow_version',
                                    mlflow: {
                                      species: parsedModel.species,
                                      model_type: parsedModel.modelType,
                                      version: linked.version,
                                    },
                                  },
                                });
                              }}
                            >
                              Test this model
                            </Button>
                          ) : null}
                          <Button
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                            onClick={() => onInspect(linked.name, linked.version)}
                          >
                            Inspect
                          </Button>
                        </div>
                      ) : null}
                      {artifacts.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {artifacts.map((artifact) => (
                            <Button
                              key={artifact.id}
                              onClick={async () => {
                                const res = await api.dataviewer.artifactUrl(String(job.job_id), artifact.id);
                                window.open(res.data.url, '_blank');
                              }}
                              style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                            >
                              ⤓ {artifact.id}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
